import { apiClient } from './client';

export type DocBrandingStatus =
  | 'queued'
  | 'extracting'
  | 'planning'
  | 'checking'
  | 'fixing'
  | 'ready'
  | 'failed'
  | 'cancelled';

export const TERMINAL_STATUSES: DocBrandingStatus[] = ['ready', 'failed', 'cancelled'];

export function isRunActive(status: DocBrandingStatus): boolean {
  return !TERMINAL_STATUSES.includes(status);
}

export interface DocBrandingFinding {
  page: number;
  specIndex: number;
  severity: 'error' | 'warning';
  code: string;
  message: string;
  source: 'layout' | 'brand' | 'content' | 'visual' | 'fidelity';
}

export interface DocBrandingRound {
  round: number;
  pageCount: number;
  errors: number;
  warnings: number;
  checks: Array<'layout' | 'content' | 'visual' | 'fidelity'>;
}

export interface DocBrandingRun {
  id: string;
  requestedByUserId: string | null;
  requestedByName: string | null;
  sourceFilename: string;
  sourceKind: string;
  sourceBytes: number;
  options: { documentLabel?: string; dateLabel: string; confidential: boolean; note?: string };
  status: DocBrandingStatus;
  progressMessage: string | null;
  sourceNotes: string[];
  rounds: DocBrandingRound[];
  findings: DocBrandingFinding[];
  pageCount: number | null;
  outputKey: string | null;
  previewKeys: string[];
  errorMessage: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  durationMs: number | null;
  createdAt: string;
}

export interface StartBrandingInput {
  file: File;
  documentLabel?: string;
  dateLabel?: string;
  confidential: boolean;
  note?: string;
}

export async function listBrandingRuns(): Promise<{ runs: DocBrandingRun[]; truncated: boolean }> {
  return apiClient.get('/doc-branding/runs');
}

export async function startBrandingRun(input: StartBrandingInput): Promise<{ runId: string }> {
  const form = new FormData();
  form.append('file', input.file);
  if (input.documentLabel) form.append('documentLabel', input.documentLabel);
  if (input.dateLabel) form.append('dateLabel', input.dateLabel);
  form.append('confidential', String(input.confidential));
  if (input.note) form.append('note', input.note);

  const headers: Record<string, string> = {};
  const csrfToken = apiClient.getCsrfToken();
  if (csrfToken) headers['X-CSRF-Token'] = csrfToken;

  const response = await fetch('/api/doc-branding/runs', {
    method: 'POST',
    headers,
    body: form,
    credentials: 'include',
  });
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(body?.error?.message ?? body?.detail ?? `Upload failed (${response.status})`);
  }
  return body;
}

export async function cancelBrandingRun(id: string): Promise<void> {
  await apiClient.post(`/doc-branding/runs/${id}/cancel`);
}

export async function downloadBrandedPdf(run: DocBrandingRun): Promise<void> {
  const { blob, fileName } = await apiClient.download(`/doc-branding/runs/${run.id}/pdf`);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName ?? run.outputKey?.split('/').pop() ?? 'SEQUESTO-document.pdf';
  a.click();
  URL.revokeObjectURL(url);
}

export function pagePreviewUrl(runId: string, page: number): string {
  return `/api/doc-branding/runs/${runId}/pages/${page}`;
}
